import { h, Component, RefObject, ComponentChildren } from "preact";

import { Keyboard } from "../../foundation/enums";
import classname from "../../foundation/utils/classname";
import { stopPropagation } from "../../foundation/utils/legacy";
import DialogFooter from "../Dialog/DialogFooter";
import Portal from "../Portal";

import styles from "./OverlayDialog.module.css";

type HTMLButtonType = "submit" | "button" | "reset";

interface Props {
    baseRef?: RefObject<HTMLDivElement>;
    class?: string;
    contentClass?: string;
    containerClass?: string;
    children?: ComponentChildren;
    positiveButtonText?: string;
    negativeButtonText?: string;
    fallbackButtonText?: string;
    positiveButtonType?: HTMLButtonType;
    negativeButtonType?: HTMLButtonType;
    fallbackButtonType?: HTMLButtonType;
    positiveButtonDisabled?: boolean;
    negativeButtonDisabled?: boolean;
    fallbackButtonDisabled?: boolean;
    onPositiveButtonClick?: () => void;
    onNegativeButtonClick?: () => void;
    onFallbackButtonClick?: () => void;
    hidePositiveButton?: boolean;
    hideNegativeButton?: boolean;
    onClose?: () => void;
    width?: number;
    height?: number;
    footer?: ComponentChildren;
    showBackdrop?: boolean;
    isDismissible?: boolean;
    loading?: boolean;
    loadingText?: string;
    hideFooter?: boolean;
    footerClass?: string;
    useNewDesign?: boolean;
}

class OverlayDialog extends Component<Props, never> {
    handleKeyDown = (event: KeyboardEvent) => {
        if (event.code === Keyboard.Key.Escape) {
            event.stopPropagation();
            this.props.onClose?.();
        }
    };

    override componentDidMount() {
        if (this.props.isDismissible) {
            document.addEventListener("keydown", this.handleKeyDown, true);
        }
    }

    override componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown, true);
    }

    render() {
        const {
            baseRef,
            class: cssClass,
            contentClass,
            containerClass,
            children,
            positiveButtonText,
            negativeButtonText,
            fallbackButtonText,
            positiveButtonType,
            negativeButtonType,
            fallbackButtonType,
            positiveButtonDisabled,
            negativeButtonDisabled,
            fallbackButtonDisabled,
            onPositiveButtonClick,
            onNegativeButtonClick,
            onFallbackButtonClick,
            hideNegativeButton,
            onClose,
            width,
            height,
            footer,
            showBackdrop,
            isDismissible,
            loading,
            loadingText,
            hideFooter,
            footerClass,
            useNewDesign
        } = this.props;

        return (
            <Portal>
                <div
                    class={classname(
                        styles.overlayDialogContainer,
                        containerClass,
                        { [styles.showBackdrop]: showBackdrop,
                            [styles.useNewDesign]: useNewDesign }
                    )}
                    onClick={isDismissible ? onClose : undefined}>
                    <div
                        ref={baseRef}
                        class={classname(
                            styles.overlayDialog,
                            cssClass,
                            { loading }
                        )}
                        style={{ width, height }}
                        onClick={stopPropagation}>
                        <div class={classname(
                            styles.overlayDialogContent,
                            { [styles.hideFooter]: hideFooter },
                            contentClass
                        )}>
                            {children}
                        </div>
                        {
                            !hideFooter && (
                                <DialogFooter
                                    positiveButtonText={positiveButtonText}
                                    negativeButtonText={negativeButtonText}
                                    fallbackButtonText={fallbackButtonText}
                                    positiveButtonType={positiveButtonType}
                                    negativeButtonType={negativeButtonType}
                                    fallbackButtonType={fallbackButtonType}
                                    positiveButtonDisabled={positiveButtonDisabled}
                                    negativeButtonDisabled={negativeButtonDisabled}
                                    fallbackButtonDisabled={fallbackButtonDisabled}
                                    hideNegativeButton={hideNegativeButton}
                                    footer={footer}
                                    loading={loading}
                                    cssClass={footerClass}
                                    loadingText={loadingText}
                                    onPositiveButtonClick={onPositiveButtonClick || onClose}
                                    onNegativeButtonClick={onNegativeButtonClick || onClose}
                                    onFallbackButtonClick={onFallbackButtonClick || onClose}/>
                            )
                        }
                    </div>
                </div>
            </Portal>
        );
    }
}

OverlayDialog.defaultProps = {
    positiveButtonText: "DONE",
    negativeButtonText: "CANCEL",
    positiveButtonType: "submit",
    negativeButtonType: "submit",
    fallbackButtonType: "submit",
    hideNegativeButton: true,
    showBackdrop: true,
    isDismissible: true
};

export default OverlayDialog;
